/**
 * Magic link landing page (Phase 4 Action 44).
 *
 * Customer admin clicks the URL in their invite email and lands here.
 * URL shape: https://app.getpowermove.com/auth/magic?token=<raw>
 *
 * On mount, POSTs the token to /admin/auth/magic-link/consume. On
 * success, stores the JWT pair and navigates to /admin (dashboard) —
 * AdminAuthProvider picks the tokens up via loadTokens() in its useEffect.
 *
 * Error states have distinct copy + CTAs:
 *   - expired / already_consumed / not_found → "Request a new link"
 *     button (lands in Action 45)
 *   - tenant_not_ready → "Contact your PowerMove team" with no retry
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setTokens } from '../api/adminClient';
import { API_ORIGIN } from '../api/baseUrl';

interface ConsumeSuccess {
  data: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    user: {
      id: string;
      email: string;
      org_id: string | null;
      first_name: string | null;
      last_name: string | null;
    };
    is_first_login: boolean;
    tenant: { slug: string; name: string };
  };
}

interface ConsumeError {
  error: 'not_found' | 'already_consumed' | 'expired' | 'tenant_not_ready' | 'validation_error' | 'internal_error';
  message: string;
}

type ConsumeResult = ConsumeSuccess | ConsumeError;

function isError(r: ConsumeResult): r is ConsumeError {
  return 'error' in r;
}

export default function MagicLinkLanding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'success'; tenantName: string }
    | { kind: 'error'; code: ConsumeError['error']; message: string }
  >({ kind: 'loading' });

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setState({ kind: 'error', code: 'not_found', message: 'No token in URL.' });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_ORIGIN}/api/admin/auth/magic-link/consume`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const body = (await res.json()) as ConsumeResult;
        if (cancelled) return;

        if (!res.ok || isError(body)) {
          const err = body as ConsumeError;
          setState({ kind: 'error', code: err.error ?? 'internal_error', message: err.message });
          return;
        }

        const { access_token, refresh_token, tenant } = body.data;
        setTokens(access_token, refresh_token);
        setState({ kind: 'success', tenantName: tenant.name });

        // Brief delay so the customer reads the "Welcome to <tenant>"
        // moment before landing on the dashboard.
        setTimeout(() => navigate('/admin', { replace: true }), 1200);
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: 'error',
          code: 'internal_error',
          message: err instanceof Error ? err.message : 'Unknown error',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params, navigate]);

  if (state.kind === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
          <p className="mt-4 text-sm text-slate-600">Signing you in…</p>
        </div>
      </div>
    );
  }

  if (state.kind === 'success') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-8 text-center">
          <h1 className="text-xl font-semibold text-emerald-900">
            Welcome to {state.tenantName}
          </h1>
          <p className="mt-2 text-sm text-emerald-800">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  // Error states.
  const isRetryable =
    state.code === 'expired' ||
    state.code === 'already_consumed' ||
    state.code === 'not_found';

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="max-w-md rounded-md border border-red-200 bg-white p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900">
          {state.code === 'expired' && 'This link has expired'}
          {state.code === 'already_consumed' && 'This link has already been used'}
          {state.code === 'not_found' && "We couldn't find that link"}
          {state.code === 'tenant_not_ready' && 'Your tenant is not yet ready'}
          {(state.code === 'validation_error' || state.code === 'internal_error') && 'Something went wrong'}
        </h1>
        <p className="mt-3 text-sm text-slate-600">{state.message}</p>
        {isRetryable && (
          <div className="mt-6">
            <a
              href="/auth/magic/request-new"
              className="inline-flex rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Request a new link
            </a>
            <p className="mt-2 text-xs text-slate-500">
              We'll let your PowerMove contact know to send a fresh invite.
            </p>
          </div>
        )}
        {state.code === 'tenant_not_ready' && (
          <p className="mt-6 text-xs text-slate-500">
            Contact your PowerMove team — your tenant is being set up.
          </p>
        )}
      </div>
    </div>
  );
}
